import * as Rx from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { Collections } from '../collections.model';
import {
  BackendDeleteResponse,
  BackendPushResponse,
  BackendSetResponse,
  BackendUpdateResponse,
  StoreBackend,
} from './backend';
import { DatastoreDocument } from './datastore-document';
import { RecursivePartial } from './helpers';
import { RequestStatus } from './request-status-handler.service';
import { Reference } from './store.model';

export class DatastoreCollection<K extends keyof Collections> {
  constructor(
    private ref$: Rx.Observable<Reference<K>>,
    private storeBackend: StoreBackend,
    public status$: Rx.Observable<RequestStatus<K>>,
    private valueChanges$: Rx.Observable<ReadonlyArray<Collections[K]>>,
  ) {}

  valueChanges(): Rx.Observable<ReadonlyArray<Collections[K]>> {
    return this.valueChanges$;
  }

  push(object: Collections[K]): Promise<BackendPushResponse<K>> {
    return this.ref$
      .pipe(
        take(1),
        switchMap(ref => this.storeBackend.push(ref, object)),
      )
      .toPromise();
  }

  set(
    id: number | string,
    object: Collections[K],
  ): Promise<BackendSetResponse<K>> {
    return this.ref$
      .pipe(
        take(1),
        switchMap(ref => this.storeBackend.set(ref, id, object)),
      )
      .toPromise();
  }

  update(
    id: number | string,
    delta: RecursivePartial<Collections[K]>,
  ): Promise<BackendUpdateResponse<K>> {
    return this.ref$
      .pipe(
        take(1),
        switchMap(ref => this.storeBackend.update(ref, id, delta)),
      )
      .toPromise();
  }

  remove(id: number | string): Promise<BackendDeleteResponse<K>> {
    return this.ref$
      .pipe(
        take(1),
        switchMap(ref => this.storeBackend.delete(ref, id)),
      )
      .toPromise();
  }

  toDatastoreDocumentList(): Rx.Observable<
    ReadonlyArray<DatastoreDocument<K>>
  > {
    return this.valueChanges$.pipe(
      map(collections =>
        collections.map(
          collection =>
            new DatastoreDocument<K>(
              this.ref$.pipe(
                map(ref => ({
                  path: {
                    ...ref.path,
                    ids: [collection.id.toString()] as [string], // Type cast to get arity right
                  },
                })),
              ),
              this.storeBackend,
              this.status$,
              Rx.of(collection),
            ),
        ),
      ),
    );
  }
}
